import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCourse, useCreateCourse, useUpdateCourse } from '@/hooks/useCourses'
import { CourseForm } from '@/components/courses/CourseForm'
import type { CreateCourseRequest, UpdateCourseRequest } from '@/types/api'

export const CourseFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const { data: course, isLoading: isLoadingCourse } = useCourse(id || '')
  const createMutation = useCreateCourse()
  const updateMutation = useUpdateCourse()

  const handleSubmit = async (data: CreateCourseRequest | UpdateCourseRequest) => {
    try {
      if (isEditMode && id) {
        await updateMutation.mutateAsync({
          id,
          data: data as UpdateCourseRequest,
        })
      } else {
        await createMutation.mutateAsync(data as CreateCourseRequest)
      }
      navigate('/admin/courses')
    } catch (error) {
      console.error('Failed to save course:', error)
    }
  }

  const handleCancel = () => {
    navigate('/admin/courses')
  }

  if (isEditMode && isLoadingCourse) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-gray-500 dark:text-gray-400">Loading course...</p>
      </div>
    )
  }

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* ── Refined Header ── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        {isEditMode ? 'Edit Kursus' : 'Buat Kursus Baru'}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 font-bold text-sm tracking-widest mt-1">
                        LMS • Manajemen Pelatihan
                    </p>
                </div>
            </div>

            {/* ── Refined Form Card ── */}
            <div className="relative group">
                {/* Decorative Background Circles (Dashboard Style) */}
                <div className="absolute -top-12 -right-12 size-64 bg-primary/10 rounded-full blur-3xl -z-10 group-hover:bg-primary/20 transition-colors duration-700" />
                <div className="absolute -bottom-12 -left-12 size-64 bg-rose-500/10 rounded-full blur-3xl -z-10 group-hover:bg-rose-500/20 transition-colors duration-700" />

                <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-[2rem] border border-slate-200/50 dark:border-white/10 shadow-2xl shadow-slate-200/50 dark:shadow-none p-6 md:p-10 relative overflow-hidden">
                    {/* Top Accent Line */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-primary rounded-b-full shadow-[0_0_15px_rgba(var(--color-primary),0.3)]" />
                    
                    <CourseForm
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        initialData={course}
                        isLoading={createMutation.isPending || updateMutation.isPending}
                    />
                </div>
            </div>
        </div>
    )
}

